/**
 * @fileoverview Deactivate User dialog hook.
 * @description The Deactivate dialog's data and command: reads the user's details afresh (whose
 * ETag the command carries as If-Match), the lifecycle configuration and whether anyone reports to
 * the user, and sends the deactivation. The command may be sent once the details and configuration
 * are known and the re-read user still allows it. The direct-reports read only decides whether the
 * consequence note mentions the reports moving: while it loads the confirm button waits, so the
 * admin is told before confirming, but if it fails the admin is warned, can retry it, and may still
 * deactivate (the dialog must not be unusable because a note could not be loaded). The success
 * confirmation and the close run in the mutation's own callback, so they still happen if the dialog
 * unmounts while the request is in flight, and closing is held while the request is pending. A
 * stale-version refusal (412 / 3022) re-reads the details.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useDeactivateUser } from '@/features/users/hooks/useDeactivateUser'
import { useDirectReports } from '@/features/users/hooks/useDirectReports'
import { useFreshUserDetail } from '@/features/users/hooks/useFreshUserDetail'
import { useUserManagementConfiguration } from '@/features/users/hooks/useUserManagementConfiguration'
import { ERROR_CODES } from '@/shared/constants/error-codes.constants'
import { useGuardedClose } from '@/shared/hooks/useGuardedClose'

import type { DeactivateUserRequest, UserDetail } from '@/features/users/types/users.types'

interface DeactivateUserDialogOptions {
  userId: string
  onClose: () => void
  onDeactivated: (user: UserDetail) => void
}

/**
 * @description Loads what the Deactivate dialog needs and runs the deactivation.
 * @param {DeactivateUserDialogOptions} options - The user, and the dialog's callbacks.
 * @param {string} options.userId - The user to deactivate.
 * @param {() => void} options.onClose - Dismisses the dialog.
 * @param {(user: UserDetail) => void} options.onDeactivated - Called with the deactivated user
 * after a successful deactivation, before the dialog closes.
 * @returns {object} The loaded data, the load states, whether the command may be sent, the close
 * handler (held while the command is pending) and the command.
 */
export function useDeactivateUserDialog({
  userId,
  onClose,
  onDeactivated,
}: DeactivateUserDialogOptions) {
  const detailQuery = useFreshUserDetail(userId)
  const configurationQuery = useUserManagementConfiguration()
  const reportsQuery = useDirectReports(userId)
  const deactivateUser = useDeactivateUser({
    /**
     * @description Reports the deactivated user, then closes the dialog. Runs even when the
     * dialog has unmounted since the request was sent.
     * @param {VersionedUserDetail} result - The deactivated user and their new version.
     */
    onSuccess: ({ user }) => {
      onDeactivated(user)
      onClose()
    },
  })
  const close = useGuardedClose(onClose, deactivateUser.isPending)
  const detail = detailQuery.detail
  const configuration = configurationQuery.data
  // The list's advisory actions can be stale: once the user is re-read, only offer what's allowed.
  const allowed = detail?.user.allowedActions.includes('DEACTIVATE') ?? false
  const loadError = detailQuery.error ?? configurationQuery.error

  /**
   * @description Deactivates the user against the version the form was filled from. The
   * Idempotency-Key is kept for this action on this user while its outcome is unknown, so sending
   * it again after a lost response is a replay, not a second command.
   * @param {DeactivateUserRequest} payload - The reason and note.
   */
  const submit = (payload: DeactivateUserRequest) => {
    if (!detail) {
      return
    }
    deactivateUser.mutate(
      { userId, payload, etag: detail.etag },
      {
        /**
         * @description Re-reads the user when someone else changed them since the dialog loaded.
         * @param {ApiError} error - The refusal.
         */
        onError: (error) => {
          if (error.code === ERROR_CODES.VERSION_CONFLICT) {
            void detailQuery.refetch()
          }
        },
      },
    )
  }

  /**
   * @description Asks again whether anyone reports to the user, after the check failed.
   */
  const retryReports = () => {
    void reportsQuery.refetch()
  }

  return {
    detail,
    configuration,
    allowed,
    loadError,
    loading: (detailQuery.isPending || configurationQuery.isPending) && !loadError,
    /** Whether anyone would move; absent until the read has succeeded. */
    hasReports: reportsQuery.data,
    reportsError: reportsQuery.error,
    reportsLoading: reportsQuery.isPending,
    retryReports,
    // Waits for the reports read to settle, but a failed one doesn't block (it is warned about).
    canSubmit: Boolean(detail && configuration && allowed && !reportsQuery.isPending),
    close,
    submit,
    isSubmitting: deactivateUser.isPending,
    submitError: deactivateUser.error,
  }
}
